import React, { useEffect, useMemo, useState } from "react";
import { get, find, isEmpty, findIndex } from "lodash";
import moment from "moment-timezone";
import config from "../../../config";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import WithRouter from "../../../components/Common/WithRouter";
import Auth from "../../../utils/Auth";
import qs from "qs";
import { request } from "../../../utils/Request";
import { Link } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import "./DashboardSupplier.css";

const DEFAULT_COUNTER_WIDGET = {
    orders: 0,
    loads: 0,
    transport_vehicles: 0,
    users: 0,
};

const DEFAULT_LIST_WIDGET = {
    day_off_vehicles: [],
    transported_vehicles_by_country: [],
    transported_vehicles_by_sub_contractor: [],
    in_progress_orders: [],
    grouped_orders: [],
};

const KPI_CARDS = [
    {
        key: "orders",
        labelKey: "orders",
        to: "/orders",
        icon: "pi-shopping-cart",
        tone: "blue",
    },
    {
        key: "loads",
        labelKey: "loads",
        to: "/loads",
        icon: "pi-map",
        tone: "orange",
    },
    {
        key: "transport_vehicles",
        labelKey: "transport vehicles",
        to: "/transport/vehicles",
        icon: "pi-truck",
        tone: "green",
    },
    {
        key: "users",
        labelKey: "users",
        to: "/users",
        icon: "pi-users",
        tone: "purple",
    },
] as const;

const getUserFirstName = (user: any): string => {
    const explicitFirstName = String(user?.first_name ?? "").trim();

    if (explicitFirstName) {
        return explicitFirstName;
    }

    const name = String(user?.name ?? "").trim();

    return name ? name.split(/\s+/)[0] : "";
};

const getUserHour = (user: any): number => {
    const timezone = String(user?.timezone ?? "").trim();

    if (timezone && moment.tz.zone(timezone)) {
        return Number(moment().tz(timezone).format("H"));
    }

    return new Date().getHours();
};

const getGreetingKey = (hour: number): string => {
    if (hour >= 5 && hour < 12) {
        return "good morning";
    }

    if (hour >= 12 && hour < 17) {
        return "good afternoon";
    }

    if (hour >= 17 && hour < 21) {
        return "good evening";
    }

    return "good night";
};

type DashboardSectionProps = {
    icon: string;
    title: string;
    subtitle: string;
    children: React.ReactNode;
};

const DashboardSection = ({ icon, title, subtitle, children }: DashboardSectionProps) => (
    <section className="dashboard-section">
        <div className="dashboard-section__head">
            <span className="dashboard-section__icon">
                <i className={`pi ${icon}`} />
            </span>
            <div className="dashboard-section__title-wrap">
                <h3 className="dashboard-section__title">{title}</h3>
                <p className="dashboard-section__subtitle">{subtitle}</p>
            </div>
            <span className="dashboard-section__line" aria-hidden="true" />
        </div>
        {children}
    </section>
);

type DashboardPanelProps = {
    title: string;
    icon: string;
    tone?: string;
    featured?: boolean;
    children: React.ReactNode;
};

const DashboardPanel = ({
    title,
    icon,
    tone = "blue",
    featured = false,
    children,
}: DashboardPanelProps) => (
    <div
        className={`dashboard-panel dashboard-panel--${tone}${
            featured ? " dashboard-panel--featured" : ""
        }`}
    >
        <div className="dashboard-panel__accent" />
        <div className="dashboard-panel__header">
            <span className="dashboard-panel__icon">
                <i className={`pi ${icon}`} />
            </span>
            <h4>{title}</h4>
        </div>
        <div className="dashboard-panel__body">{children}</div>
    </div>
);

const Dashboard = (props: any) => {
    const { t } = props;
    document.title = `${t("dashboard")} | ${config.appNameForTitle}`;

    const user = useMemo(() => new Auth().getUser(), []);
    const userFirstName = useMemo(() => getUserFirstName(user), [user]);
    const greetingKey = useMemo(() => getGreetingKey(getUserHour(user)), [user]);
    const formattedDate = useMemo(() => {
        const timezone = String((user as any)?.timezone ?? "").trim();

        if (timezone && moment.tz.zone(timezone)) {
            return moment().tz(timezone).format("dddd, MMM D, YYYY");
        }

        return moment().format("dddd, MMM D, YYYY");
    }, [user]);

    const [groupedOrderByCountry, setGroupedOrderByCountry] = useState([]);
    const [groupedOrderByCity, setGroupedOrderByCity] = useState([]);
    const [counterWidget, setCounterWidget] = useState(DEFAULT_COUNTER_WIDGET);
    const [listWidget, setListWidget] = useState(DEFAULT_LIST_WIDGET);
    const [numberOfTrVehiclesByCountry, setNumberOfTrVehiclesByCountry] = useState<any>([]);
    const [numberOfTrVehiclesByTransportCompanies, setNumberOfTrVehiclesByTransportCompanies] =
        useState<any>([]);
    const [numberOfActiveLoads, setNumberOfActiveLoads] = useState<any>([]);
    const [lineChartOptions, setLineChartOptions] = useState({});
    const [pieChartOptions, setPieChartOptions] = useState({});

    const applyChartTheme = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue("--text-color");
        const textColorSecondary = documentStyle.getPropertyValue("--text-color-secondary");
        const surfaceBorder = documentStyle.getPropertyValue("--surface-border");

        setLineChartOptions({
            maintainAspectRatio: false,
            aspectRatio: 1.2,
            plugins: {
                legend: {
                    labels: {
                        color: textColor,
                    },
                },
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                    },
                },
                y: {
                    ticks: {
                        color: textColorSecondary,
                    },
                    grid: {
                        color: surfaceBorder,
                    },
                },
            },
        });

        setPieChartOptions({
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: textColor,
                    },
                },
            },
        });
    };

    const fetchListTotal = async (url: string, params: Record<string, unknown> = {}) => {
        try {
            const response = await request({
                url,
                params: {
                    page: 1,
                    per_page: 1,
                    ...params,
                },
                paramsSerializer: {
                    serialize: (requestParams: Record<string, unknown>) =>
                        qs.stringify(requestParams),
                },
            });

            return response.data?.meta?.total ?? 0;
        } catch {
            return 0;
        }
    };

    const getDashboardCounter = async () => {
        try {
            const [orders, loads, transportVehicles, users] = await Promise.all([
                fetchListTotal("/suppliers/orders?1=1", {
                    filters: { statuses: ["produced"] },
                }),
                fetchListTotal("/suppliers/loads?1=1"),
                fetchListTotal("/suppliers/transport/vehicles?1=1"),
                fetchListTotal("/suppliers/users?1=1"),
            ]);

            setCounterWidget({
                orders,
                loads,
                transport_vehicles: transportVehicles,
                users,
            });
        } catch (e) {
            // ignore
        }
    };

    const getDashboardList = async () => {
        try {
            const response = await request({
                url: "/suppliers/dashboards/list",
                params: {},
            });

            const { data } = response.data;
            setListWidget(data);

            if (data.transported_vehicles_by_country) {
                const xBar: string[] = [];
                const series: number[] = [];

                data.transported_vehicles_by_country.map((item: any) => {
                    xBar.push(item.country);
                    series.push(item.vehicles);
                });

                setNumberOfTrVehiclesByCountry({
                    labels: xBar,
                    datasets: [
                        {
                            label: t("total transportation"),
                            data: series,
                            backgroundColor: "rgba(59, 130, 246, 0.72)",
                            borderColor: "#3b82f6",
                            borderWidth: 1,
                            borderRadius: 6,
                        },
                    ],
                });
            }

            if (data.in_progress_orders) {
                setNumberOfActiveLoads({
                    labels: [t("total"), t("in transit")],
                    datasets: [
                        {
                            data: [
                                data.in_progress_orders.total,
                                data.in_progress_orders.in_progress,
                            ],
                            backgroundColor: ["#3b82f6", "#f59e0b"],
                        },
                    ],
                });
            }

            if (data.transported_vehicles_by_sub_contractor) {
                const xBar: string[] = [];
                const series: number[] = [];

                data.transported_vehicles_by_sub_contractor.map((item: any) => {
                    xBar.push(item.name);
                    series.push(Number(item.vehicles));
                });

                setNumberOfTrVehiclesByTransportCompanies({
                    labels: xBar,
                    datasets: [
                        {
                            data: series,
                            backgroundColor: [
                                "#3b82f6",
                                "#10b981",
                                "#f59e0b",
                                "#8b5cf6",
                                "#06b6d4",
                                "#ef4444",
                            ],
                        },
                    ],
                });
            }

            if (data.grouped_orders && data.grouped_orders.country) {
                const newArray: any = [];
                data.grouped_orders.country.map((order: any) => {
                    const { country, status, number_of_orders } = order;
                    let item: any = {
                        country,
                        announced: 0,
                        inTransit: 0,
                        delivered: 0,
                        total: 0,
                    };
                    const existingItem: any = find(newArray, { country: country });

                    if (!isEmpty(existingItem)) {
                        item = existingItem;
                    }

                    switch (status) {
                        case "announced":
                            item.announced += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        case "in-transit":
                            item.inTransit += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        case "delivered":
                            item.delivered += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        default:
                            break;
                    }

                    if (!isEmpty(existingItem)) {
                        newArray[findIndex(newArray, { country: country })] = item;
                    } else {
                        newArray.push(item);
                    }
                }, {});

                setGroupedOrderByCountry(newArray);
            }

            if (data.grouped_orders && data.grouped_orders.city) {
                const newArray: any = [];
                data.grouped_orders.city.map((order: any) => {
                    const { city, status, number_of_orders } = order;
                    let item: any = {
                        city,
                        announced: 0,
                        inTransit: 0,
                        delivered: 0,
                        total: 0,
                    };
                    const existingItem: any = find(newArray, { city: city });

                    if (!isEmpty(existingItem)) {
                        item = existingItem;
                    }

                    switch (status) {
                        case "announced":
                            item.announced += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        case "in-transit":
                            item.inTransit += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        case "delivered":
                            item.delivered += number_of_orders;
                            item.total += number_of_orders;
                            break;
                        default:
                            break;
                    }

                    if (!isEmpty(existingItem)) {
                        newArray[findIndex(newArray, { city: city })] = item;
                    } else {
                        newArray.push(item);
                    }
                }, {});
                setGroupedOrderByCity(newArray);
            }
        } catch (e) {
            // ignore
        }
    };

    useEffect(() => {
        async function init() {
            applyChartTheme();
            await getDashboardCounter();
            await getDashboardList();
        }

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="supplier-dashboard">
            <div className="dashboard-hero">
                <div className="dashboard-hero__mesh" aria-hidden="true" />
                <div className="dashboard-hero__grid" aria-hidden="true" />
                <div className="dashboard-hero__content">
                    <span className="dashboard-hero__badge">
                        <i className="pi pi-calendar" />
                        {formattedDate}
                    </span>
                    <h2>
                        {t(greetingKey)}
                        {userFirstName ? `, ${userFirstName}` : ""}
                    </h2>
                    <p>{t("welcome back quick overview")}</p>
                </div>
                <div className="dashboard-hero__visual" aria-hidden="true">
                    <svg className="dashboard-hero__route" viewBox="0 0 168 120">
                        <path
                            className="dashboard-hero__route-path"
                            d="M18 88 C 48 88, 58 28, 92 28 S 142 58, 150 38"
                        />
                        <circle className="dashboard-hero__route-dot" cx="18" cy="88" r="5" />
                    </svg>
                    <span className="dashboard-hero__float-icon dashboard-hero__float-icon--truck">
                        <i className="pi pi-truck" />
                    </span>
                    <span className="dashboard-hero__float-icon dashboard-hero__float-icon--box">
                        <i className="pi pi-box" />
                    </span>
                    <span className="dashboard-hero__float-icon dashboard-hero__float-icon--pin">
                        <i className="pi pi-map-marker" />
                    </span>
                </div>
            </div>

            <DashboardSection
                icon="pi-th-large"
                title={t("overview")}
                subtitle={t("dashboard overview subtitle")}
            >
                <Row className="g-3">
                    {KPI_CARDS.map((card, index) => (
                        <Col key={card.key} sm={12} md={6} xl={3}>
                            <Link
                                to={card.to}
                                className={`dashboard-kpi dashboard-kpi--${card.tone}`}
                                style={{ animationDelay: `${index * 0.08}s` }}
                            >
                                <i
                                    className={`pi ${card.icon} dashboard-kpi__watermark`}
                                    aria-hidden="true"
                                />
                                <span className="dashboard-kpi__icon">
                                    <i className={`pi ${card.icon}`} />
                                </span>
                                <span className="dashboard-kpi__body">
                                    <span className="dashboard-kpi__label">
                                        {t(card.labelKey)}
                                    </span>
                                    <span className="dashboard-kpi__value">
                                        {counterWidget[card.key as keyof typeof counterWidget]}
                                    </span>
                                </span>
                                <span className="dashboard-kpi__cta">
                                    <i className="pi pi-arrow-right" />
                                </span>
                            </Link>
                        </Col>
                    ))}
                </Row>
            </DashboardSection>

            <DashboardSection
                icon="pi-sitemap"
                title={t("operations")}
                subtitle={t("dashboard operations subtitle")}
            >
                <Row className="g-3">
                    <Col sm={12} xl={6}>
                        <DashboardPanel
                            title={t("vehicle status by country")}
                            icon="pi-globe"
                            tone="blue"
                        >
                            <DataTable
                                value={groupedOrderByCountry}
                                dataKey="id"
                                paginator
                                rows={9}
                                className="datatable-responsive"
                                emptyMessage={t("no data")}
                            >
                                <Column
                                    field="country"
                                    header={t("country")}
                                    bodyClassName="text-capitalize"
                                    sortable
                                />
                                <Column field="announced" header={t("announced")} sortable />
                                <Column field="inTransit" header={t("in transit")} sortable />
                                <Column field="delivered" header={t("delivered")} sortable />
                                <Column field="total" header={t("total")} sortable />
                            </DataTable>
                        </DashboardPanel>
                    </Col>

                    <Col sm={12} xl={6}>
                        <DashboardPanel
                            title={t("vehicle status by city")}
                            icon="pi-map-marker"
                            tone="cyan"
                        >
                            <DataTable
                                value={groupedOrderByCity}
                                dataKey="id"
                                paginator
                                rows={9}
                                className="datatable-responsive"
                                emptyMessage={t("no data")}
                            >
                                <Column
                                    field="city"
                                    header={t("city")}
                                    bodyClassName="text-capitalize"
                                    sortable
                                />
                                <Column field="announced" header={t("announced")} sortable />
                                <Column field="inTransit" header={t("in transit")} sortable />
                                <Column field="delivered" header={t("delivered")} sortable />
                                <Column field="total" header={t("total")} sortable />
                            </DataTable>
                        </DashboardPanel>
                    </Col>

                    <Col sm={12}>
                        <DashboardPanel
                            title={t("vehicle with days out of use")}
                            icon="pi-clock"
                            tone="amber"
                        >
                            <DataTable
                                value={listWidget.day_off_vehicles}
                                dataKey="id"
                                paginator
                                rows={9}
                                className="datatable-responsive"
                                emptyMessage={t("no data")}
                            >
                                <Column
                                    field="truck_plate_number"
                                    header={t("truck plate number")}
                                />
                                <Column
                                    field="trailer_plate_number"
                                    header={t("trailer plate number")}
                                />
                                <Column
                                    field="days"
                                    header={t("days")}
                                    body={(_row: any) => {
                                        const days = _row.days_since_last_journey.split(" ");
                                        return get(days, [1]) == "days"
                                            ? get(days, [0]) + " " + get(days, [1])
                                            : 0;
                                    }}
                                />
                            </DataTable>
                        </DashboardPanel>
                    </Col>
                </Row>
            </DashboardSection>

            <DashboardSection
                icon="pi-chart-line"
                title={t("analytics")}
                subtitle={t("dashboard analytics subtitle")}
            >
                <Row className="g-3">
                    <Col sm={12}>
                        <DashboardPanel
                            title={t("transported vehicles by country")}
                            icon="pi-chart-bar"
                            tone="indigo"
                            featured
                        >
                            <div className="dashboard-chart-wrap">
                                <Chart
                                    type="bar"
                                    data={numberOfTrVehiclesByCountry}
                                    options={lineChartOptions}
                                    height="320"
                                />
                            </div>
                        </DashboardPanel>
                    </Col>

                    <Col sm={12} xl={7}>
                        <DashboardPanel
                            title={t("transported vehicles by transport companies")}
                            icon="pi-chart-pie"
                            tone="violet"
                        >
                            <div className="dashboard-chart-wrap dashboard-chart-wrap--compact">
                                <Chart
                                    type="pie"
                                    data={numberOfTrVehiclesByTransportCompanies}
                                    options={pieChartOptions}
                                    height="280"
                                />
                            </div>
                        </DashboardPanel>
                    </Col>

                    <Col sm={12} xl={5}>
                        <DashboardPanel
                            title={t("active loads")}
                            icon="pi-bolt"
                            tone="rose"
                        >
                            <div className="dashboard-chart-wrap dashboard-chart-wrap--compact">
                                <Chart
                                    type="doughnut"
                                    data={numberOfActiveLoads}
                                    options={pieChartOptions}
                                    height="280"
                                />
                            </div>
                        </DashboardPanel>
                    </Col>
                </Row>
            </DashboardSection>
        </div>
    );
};

Dashboard.propTypes = {
    history: PropTypes.object,
    t: PropTypes.any,
};

export default withTranslation()(WithRouter(Dashboard));
